/**
 * Allys Footer
 * 
 * Modern, SEO-optimized footer with Allys branding
 * Features large logo watermark with special effects
 */

'use client';

import { FacebookLogo, InstagramLogo } from '@phosphor-icons/react';
import Image from 'next/image';
import Link from 'next/link';
import { useState } from 'react';
import type { CoreFooterProps } from '../../../../src/components/navigation/Footer';

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const PHONE_PATTERN = /^[+\d\s().-]+$/;
const SAFE_URL_PATTERN = /^https?:\/\//i;

export interface AllysFooterProps extends Omit<CoreFooterProps, 'className' | 'logoComponent'> {
  /**
   * Optional custom logo URL (defaults to Allys logo)
   */
  logoUrl?: string | undefined;
  
  /**
   * Show/hide the large logo watermark
   * @default true
   */
  showLogoWatermark?: boolean;
  
  /**
   * Messages for translations
   */
  messages?: Record<string, unknown>;
  
  /**
   * Current locale
   */
  locale?: 'en' | 'es' | 'fr';
}

// Helper to safely get string from messages
const safeGetString = (obj: unknown, defaultValue: string): string => {
  if (typeof obj === 'string') return obj;
  return defaultValue;
};

/**
 * Allys Footer Component
 * 
 * Modern, clean footer with Allys design system
 * Includes large SVG logo watermark with special effects
 */
export function AllysFooter({
  companyName,
  tagline,
  contactEmail,
  contactPhone,
  address,
  socialLinks,
  legalLinks: _legalLinks = [],
  quickLinks: _quickLinks = [],
  logoUrl = 'https://res.cloudinary.com/elementary-media/image/upload/v1762241496/Allys_Logo_Horizontal_kjkzt2.svg',
  showLogoWatermark = true,
  newsletter: _newsletter,
  renderCustomSections: _renderCustomSections,
  children,
  messages,
  locale = 'en',
}: AllysFooterProps) {
  // Get translations from messages
  const footerMsg = messages?.footer as Record<string, unknown> | undefined;
  const sections = footerMsg?.sections as Record<string, unknown> | undefined;
  const propertyTypes = footerMsg?.propertyTypes as Record<string, unknown> | undefined;
  const quickLinksMsg = footerMsg?.quickLinks as Record<string, unknown> | undefined;
  const contactLabelsMsg = footerMsg?.contactLabels as Record<string, unknown> | undefined;
  const coastsMsg = footerMsg?.coasts as Record<string, unknown> | undefined;
  const seoMsg = footerMsg?.seoSection as Record<string, unknown> | undefined;
  const legalLinksMsg = footerMsg?.legalLinks as Record<string, unknown> | undefined;
  
  const displayTagline = tagline || safeGetString(footerMsg?.tagline, 'Your trusted partner in Mauritius real estate');

  const resolvedAddress = address || {
    street: 'Royal Road, Beau Bassin-Rose Hill',
    city: 'Beau Bassin-Rose Hill',
    postalCode: '71504',
    country: 'MU',
  };

  const resolvedSocialLinks = socialLinks || {
    facebook: 'https://www.facebook.com/AllysPropertyMauritius',
    instagram: 'https://www.instagram.com/allys_property',
  };
  const [email, setEmail] = useState('');
  const [subscribeStatus, setSubscribeStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');

  const handleNewsletterSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      setSubscribeStatus('error');
      setTimeout(() => setSubscribeStatus('idle'), 3000);
      return;
    }

    setSubscribeStatus('loading');
    try {
      const res = await fetch('/api/newsletter/subscribe', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email }),
      });
      if (!res.ok) throw new Error('Subscription failed');
      setSubscribeStatus('success');
      setEmail('');

      if (typeof window !== 'undefined' && window.gtag) {
        window.gtag('event', 'generate_lead', {
          event_category: 'newsletter',
          event_label: 'footer_signup',
        });
      }
    } catch {
      setSubscribeStatus('error');
    }
    setTimeout(() => setSubscribeStatus('idle'), 4000);
  };

  const currentYear = new Date().getFullYear();

  return (
    <footer 
      className="relative bg-white overflow-hidden"
      role="contentinfo"
      aria-label="Site footer"
    >
      {/* Large Logo Watermark - Special Effect */}
      {showLogoWatermark && (
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none overflow-hidden">
          <div className="relative w-full max-w-6xl opacity-[0.02] dark:opacity-[0.03]">
            <Image
              src={logoUrl}
              alt=""
              width={1200}
              height={1200}
              className="w-full h-auto mix-blend-multiply dark:mix-blend-lighten transition-all duration-1000"
              priority={false}
              aria-hidden="true"
            />
            {/* Gradient overlay for extra depth */}
            <div className="absolute inset-0 bg-gradient-to-br from-[var(--color-primary)]/5 via-transparent to-[var(--color-accent)]/5" />
          </div>
        </div>
      )}

      {/* Main Footer Content */}
      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 lg:py-28">
        
        {/* Top Row: Company Info + Property Types + Company Menu */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-12 mb-16">
          
          {/* Company Info */}
          <div>
            {/* Logo */}
            <div className="mb-8">
              <Image
                src={logoUrl}
                alt={`${companyName} Logo`}
                width={200}
                height={50}
                className="h-12 w-auto transition-transform duration-500 hover:scale-105 drop-shadow-lg"
                priority
              />
            </div>
            
            {displayTagline && (
              <p 
                className="text-base text-[var(--color-text-muted)] mb-8 leading-relaxed max-w-md font-inter"
              >
                {displayTagline}
              </p>
            )}

            {/* Contact Information */}
            {address && (
              <div className="mb-6 space-y-2 text-sm text-[var(--color-text-muted)]">
                <div className="flex items-start gap-3 group">
                  <svg className="w-5 h-5 mt-0.5 flex-shrink-0 text-[var(--color-text-muted)] group-hover:text-[var(--color-primary)] transition-colors" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M15 10.5a3 3 0 11-6 0 3 3 0 016 0z" />
                    <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 10.5c0 7.142-7.5 11.25-7.5 11.25S4.5 17.642 4.5 10.5a7.5 7.5 0 1115 0z" />
                  </svg>
                  <div className="font-inter">
                    <p>{address.street}</p>
                    <p>{address.city}{address.postalCode && `, ${address.postalCode}`}</p>
                  </div>
                </div>
              </div>
            )}

            <div className="space-y-3 text-sm">
              {contactEmail && EMAIL_PATTERN.test(contactEmail) && (
                <a 
                  href={`mailto:${contactEmail}`}
                  className="flex items-center gap-3 text-[var(--color-text-muted)] hover:text-[var(--color-primary)] transition-colors group font-inter"
                  aria-label={`Email us at ${contactEmail}`}
                >
                  <svg className="w-5 h-5 flex-shrink-0 text-[var(--color-text-muted)] group-hover:text-[var(--color-primary)] transition-colors" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5} aria-hidden="true">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M21.75 6.75v10.5a2.25 2.25 0 01-2.25 2.25h-15a2.25 2.25 0 01-2.25-2.25V6.75m19.5 0A2.25 2.25 0 0019.5 4.5h-15a2.25 2.25 0 00-2.25 2.25m19.5 0v.243a2.25 2.25 0 01-1.07 1.916l-7.5 4.615a2.25 2.25 0 01-2.36 0L3.32 8.91a2.25 2.25 0 01-1.07-1.916V6.75" />
                  </svg>
                  <span>{contactEmail}</span>
                </a>
              )}
              
              {contactPhone && PHONE_PATTERN.test(contactPhone) && (
                <a 
                  href={`tel:${contactPhone}`}
                  className="flex items-center gap-3 text-[var(--color-text-muted)] hover:text-[var(--color-primary)] transition-colors group font-inter"
                >
                  <svg className="w-5 h-5 flex-shrink-0 text-[var(--color-text-muted)] group-hover:text-[var(--color-primary)] transition-colors" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 6.75c0 8.284 6.716 15 15 15h2.25a2.25 2.25 0 002.25-2.25v-1.372c0-.516-.351-.966-.852-1.091l-4.423-1.106c-.44-.11-.902.055-1.173.417l-.97 1.293c-.282.376-.769.542-1.21.38a12.035 12.035 0 01-7.143-7.143c-.162-.441.004-.928.38-1.21l1.293-.97c.363-.271.527-.734.417-1.173L6.963 3.102a1.125 1.125 0 00-1.091-.852H4.5A2.25 2.25 0 002.25 4.5v2.25z" />
                  </svg>
                  <span>{contactPhone}</span>
                </a>
              )}
              </div>

            {/* Social Media Icons */}
            {Object.keys(resolvedSocialLinks).length > 0 && (
              <div className="flex gap-3 mt-8">
                {resolvedSocialLinks.facebook && SAFE_URL_PATTERN.test(resolvedSocialLinks.facebook) && (
                  <a 
                    href={resolvedSocialLinks.facebook}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="group flex items-center justify-center w-11 h-11 rounded-full border border-gray-300 text-gray-600 hover:border-[#1877F2] hover:text-[#1877F2] hover:bg-[#1877F2]/5 transition-all duration-300 hover:scale-110 hover:-translate-y-1"
                    aria-label="Facebook"
                  >
                    <FacebookLogo size={20} weight="light" className="transition-transform duration-300 group-hover:scale-110" />
                  </a>
                )}
                {resolvedSocialLinks.instagram && SAFE_URL_PATTERN.test(resolvedSocialLinks.instagram) && (
                  <a 
                    href={resolvedSocialLinks.instagram}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="group flex items-center justify-center w-11 h-11 rounded-full border border-gray-300 text-gray-600 hover:border-pink-600 hover:text-pink-600 hover:bg-pink-50 transition-all duration-300 hover:scale-110 hover:-translate-y-1"
                    aria-label="Instagram"
                  >
                    <InstagramLogo size={20} weight="light" className="transition-transform duration-300 group-hover:scale-110" />
                  </a>
                )}
              </div>
            )}
          </div>

          {/* Property Types */}
          <div>
            <h3 
              className="text-xs font-bold text-[var(--color-text)] uppercase tracking-[0.15em] mb-6 pb-2 border-b-2 border-[#BBA339]/30 font-inter"
            >
              {safeGetString(sections?.propertyTypes, 'Property Types')}
            </h3>
            <ul className="space-y-3">
              {[
                { label: safeGetString(propertyTypes?.luxury, 'Luxury Villas'), href: `/${locale}/properties?type=villa` },
                { label: safeGetString(propertyTypes?.residential, 'Residential'), href: `/${locale}/properties?type=residential` },
                { label: safeGetString(propertyTypes?.apartments, 'Apartments'), href: `/${locale}/properties?type=apartment` },
                { label: safeGetString(propertyTypes?.commercial, 'Commercial'), href: `/${locale}/properties?type=commercial` },
                { label: safeGetString(propertyTypes?.land, 'Land'), href: `/${locale}/properties?type=land` },
              ].map((link) => (
                <li key={link.href}>
                  <Link
                    href={link.href}
                    className="text-[var(--color-text-muted)] hover:text-[var(--color-primary)] transition-all duration-200 text-sm flex items-center gap-2 group hover:translate-x-1 font-inter"
                  >
                    <span className="w-0 group-hover:w-2 h-px bg-linear-to-r from-(--color-primary) to-(--color-accent) transition-all duration-300"></span>
                    {link.label}
                </Link>
              </li>
              ))}
            </ul>
          </div>

          {/* Company */}
          <div>
            <h3 
              className="text-xs font-bold text-[var(--color-text)] uppercase tracking-[0.15em] mb-6 pb-2 border-b-2 border-[#BBA339]/30 font-inter"
            >
              {safeGetString(sections?.company, 'Company')}
            </h3>
            <ul className="space-y-3">
              {[
                { label: safeGetString(quickLinksMsg?.home, 'Home'), href: `/${locale}` },
                { label: safeGetString(quickLinksMsg?.properties, 'Properties'), href: `/${locale}/properties` },
                { label: safeGetString(quickLinksMsg?.about, 'About Us'), href: `/${locale}/about` },
                { label: safeGetString(quickLinksMsg?.contact, 'Contact'), href: `/${locale}/contact` },
                { label: safeGetString(quickLinksMsg?.currencyConverter, 'Currency Converter'), href: `/${locale}/currency-converter` },
              ].map((link) => (
                <li key={link.href}>
                  <Link
                    href={link.href}
                    className="text-[var(--color-text-muted)] hover:text-[var(--color-primary)] transition-all duration-200 text-sm flex items-center gap-2 group hover:translate-x-1 font-inter"
                  >
                    <span className="w-0 group-hover:w-2 h-px bg-linear-to-r from-(--color-primary) to-(--color-accent) transition-all duration-300"></span>
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* Newsletter Signup */}
        <div className="mb-16 pb-12 border-b border-gray-200/50">
          <div className="flex flex-col md:flex-row items-start md:items-center gap-4 md:gap-8">
            <div className="flex-shrink-0">
              <h3 className="text-sm font-bold text-[var(--color-text)] font-inter">
                {safeGetString(footerMsg?.newsletterTitle, 'Stay Updated')}
              </h3>
              <p className="text-xs text-[var(--color-text-muted)] mt-1">
                {safeGetString(footerMsg?.newsletterSubtitle, 'Get the latest property updates and market insights.')}
              </p>
            </div>
            {subscribeStatus === 'success' ? (
              <p className="text-sm text-green-600 font-medium">
                {safeGetString(footerMsg?.newsletterSuccess, 'Thank you for subscribing!')}
              </p>
            ) : (
              <form onSubmit={handleNewsletterSubmit} className="flex flex-col sm:flex-row gap-2 flex-1 max-w-md w-full">
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder={safeGetString(footerMsg?.newsletterPlaceholder, 'Enter your email')}
                  className="flex-1 px-4 py-2.5 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-[var(--color-primary)]/20 focus:border-[var(--color-primary)] outline-none"
                  required
                />
                <button
                  type="submit"
                  disabled={subscribeStatus === 'loading'}
                  className="px-5 py-2.5 bg-[var(--color-primary)] text-white text-sm font-medium rounded-lg hover:opacity-90 disabled:opacity-50 transition-opacity whitespace-nowrap"
                >
                  {subscribeStatus === 'loading'
                    ? safeGetString(footerMsg?.newsletterSubmitting, 'Subscribing...')
                    : safeGetString(footerMsg?.newsletterButton, 'Subscribe')}
                </button>
              </form>
            )}
            {subscribeStatus === 'error' && (
              <p className="text-xs text-red-500">
                {safeGetString(footerMsg?.newsletterError, 'Please enter a valid email address.')}
              </p>
            )}
          </div>
        </div>

        {/* Contact Our Team - Horizontal Section */}
        <div className="mb-16 pb-12 border-b border-gray-200/50">
          <div>
            <h3 
              className="text-sm font-bold text-[var(--color-text)] mb-6 font-inter"
            >
              {safeGetString(footerMsg?.contactTeam, 'Contact Our Team')}
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {/* Yalda Sheri */}
              <div className="bg-gray-50/50 rounded-2xl p-6 hover:shadow-md transition-all duration-300 border border-gray-100">
                <p className="text-base font-bold text-[var(--color-text)] mb-4 font-inter">
                  Yalda Sheri
                </p>
                <div className="space-y-2">
                  <p className="text-sm text-[var(--color-text-muted)] font-inter">
                    {safeGetString(contactLabelsMsg?.tel, 'Tel:')} <a href="tel:+23052584239" className="hover:text-[var(--color-primary)] transition-colors font-medium">+230 52584239</a>
                  </p>
                  <p className="text-sm text-[var(--color-text-muted)] font-inter">
                    {safeGetString(contactLabelsMsg?.email, 'Email:')} <a href="mailto:yalda@allys.mu" className="hover:text-[var(--color-primary)] transition-colors font-medium">yalda@allys.mu</a>
                  </p>
                </div>
              </div>

              {/* Junaid Nuzeebun */}
              <div className="bg-gray-50/50 rounded-2xl p-6 hover:shadow-md transition-all duration-300 border border-gray-100">
                <p className="text-base font-bold text-[var(--color-text)] mb-4 font-inter">
                  Junaid Nuzeebun
                </p>
                <div className="space-y-2">
                  <p className="text-sm text-[var(--color-text-muted)] font-inter">
                    {safeGetString(contactLabelsMsg?.tel, 'Tel:')} <a href="tel:+23052584240" className="hover:text-[var(--color-primary)] transition-colors font-medium">+230 52584240</a>
                  </p>
                  <p className="text-sm text-[var(--color-text-muted)] font-inter">
                    {safeGetString(contactLabelsMsg?.email, 'Email:')} <a href="mailto:junaid@allys.mu" className="hover:text-[var(--color-primary)] transition-colors font-medium">junaid@allys.mu</a>
                  </p>
                </div>
              </div>

              {/* Corporate Headquarters */}
              <div className="bg-gray-50/50 rounded-2xl p-6 hover:shadow-md transition-all duration-300 border border-gray-100">
                <p className="text-base font-bold text-[var(--color-text)] mb-4 font-inter">
                  {safeGetString(contactLabelsMsg?.headquarters, 'Corporate Headquarters')}
                </p>
                <div className="space-y-2">
                  <p className="text-sm text-[var(--color-text-muted)] font-inter">
                    {safeGetString(contactLabelsMsg?.address, 'Address:')} Beau Bassin-Rose Hill, Mauritius
                  </p>
                  <p className="text-sm text-[var(--color-text-muted)] font-inter">
                    {safeGetString(contactLabelsMsg?.phone, 'Phone:')} <a href="tel:+2304600909" className="hover:text-[var(--color-primary)] transition-colors font-medium">+230 460 0909</a>
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Coastal Areas - 5 Columns */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-8 mb-16 pb-12 border-b border-gray-200/50">
          
          {/* North Coast */}
          <div>
            <h3 
              className="text-xs font-bold text-[var(--color-text)] uppercase tracking-[0.15em] mb-6 pb-2 border-b-2 border-[#BBA339]/30 font-inter"
            >
              {safeGetString(coastsMsg?.north, 'North Coast')}
            </h3>
            <ul className="space-y-3">
              {[
                { label: 'Grand Baie', href: `/${locale}/areas/grand-baie` },
                { label: 'Pereybere', href: `/${locale}/areas/pereybere` },
                { label: 'Trou aux Biches', href: `/${locale}/areas/trou-aux-biches` },
                { label: 'Mont Choisy', href: `/${locale}/areas/mont-choisy` },
                { label: 'Balaclava', href: `/${locale}/areas/balaclava` },
                { label: 'Cap Malheureux', href: `/${locale}/areas/cap-malheureux` },
              ].map((link) => (
                <li key={link.href}>
                  <Link
                    href={link.href}
                    className="text-[var(--color-text-muted)] hover:text-[var(--color-primary)] transition-all duration-200 text-sm flex items-center gap-2 group hover:translate-x-1 font-inter"
                  >
                    <span className="w-0 group-hover:w-2 h-px bg-linear-to-r from-(--color-primary) to-(--color-accent) transition-all duration-300"></span>
                    {link.label}
                </Link>
              </li>
              ))}
            </ul>
          </div>

          {/* East Coast */}
          <div>
            <h3 
              className="text-xs font-bold text-[var(--color-text)] uppercase tracking-[0.15em] mb-6 pb-2 border-b-2 border-[#BBA339]/30 font-inter"
            >
              {safeGetString(coastsMsg?.east, 'East Coast')}
            </h3>
            <ul className="space-y-3">
              {[
                { label: 'Belle Mare', href: `/${locale}/areas/belle-mare` },
                { label: 'Palmar', href: `/${locale}/areas/palmar` },
                { label: 'Trou d\'Eau Douce', href: `/${locale}/areas/trou-deau-douce` },
                { label: 'Poste Lafayette', href: `/${locale}/areas/poste-lafayette` },
                { label: 'Roches Noires', href: `/${locale}/areas/roches-noires` },
                { label: 'Ile aux Cerfs', href: `/${locale}/areas/ile-aux-cerfs` },
              ].map((link) => (
                <li key={link.href}>
                  <Link
                    href={link.href}
                    className="text-[var(--color-text-muted)] hover:text-[var(--color-primary)] transition-all duration-200 text-sm flex items-center gap-2 group hover:translate-x-1 font-inter"
                  >
                    <span className="w-0 group-hover:w-2 h-px bg-linear-to-r from-(--color-primary) to-(--color-accent) transition-all duration-300"></span>
                    {link.label}
                </Link>
              </li>
              ))}
            </ul>
          </div>

          {/* South Coast */}
          <div>
                <h3 
                  className="text-xs font-bold text-[var(--color-text)] uppercase tracking-[0.15em] mb-6 pb-2 border-b-2 border-[#BBA339]/30 font-inter"
                >
              {safeGetString(coastsMsg?.south, 'South Coast')}
                </h3>
                <ul className="space-y-3">
              {[
                { label: 'Blue Bay', href: `/${locale}/areas/blue-bay` },
                { label: 'Mahebourg', href: `/${locale}/areas/mahebourg` },
                { label: 'Pointe d\'Esny', href: `/${locale}/areas/pointe-desny` },
                { label: 'Baie du Cap', href: `/${locale}/areas/baie-du-cap` },
                { label: 'Riambel', href: `/${locale}/areas/riambel` },
                { label: 'Bel Ombre', href: `/${locale}/areas/bel-ombre` },
              ].map((link) => (
                    <li key={link.href}>
                      <Link
                        href={link.href}
                        className="text-[var(--color-text-muted)] hover:text-[var(--color-primary)] transition-all duration-200 text-sm flex items-center gap-2 group hover:translate-x-1 font-inter"
                      >
                    <span className="w-0 group-hover:w-2 h-px bg-linear-to-r from-(--color-primary) to-(--color-accent) transition-all duration-300"></span>
                        {link.label}
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
            
          {/* West Coast */}
              <div>
                <h3 
                  className="text-xs font-bold text-[var(--color-text)] uppercase tracking-[0.15em] mb-6 pb-2 border-b-2 border-[#BBA339]/30 font-inter"
                >
              {safeGetString(coastsMsg?.west, 'West Coast')}
                </h3>
                <ul className="space-y-3">
              {[
                { label: 'Flic en Flac', href: `/${locale}/areas/flic-en-flac` },
                { label: 'Tamarin', href: `/${locale}/areas/tamarin` },
                { label: 'Black River', href: `/${locale}/areas/black-river` },
                { label: 'Le Morne', href: `/${locale}/areas/le-morne` },
                { label: 'Albion', href: `/${locale}/areas/albion` },
                { label: 'Cascavelle', href: `/${locale}/areas/cascavelle` },
              ].map((link) => (
                    <li key={link.href}>
                      <Link
                        href={link.href}
                        className="text-[var(--color-text-muted)] hover:text-[var(--color-primary)] transition-all duration-200 text-sm flex items-center gap-2 group hover:translate-x-1 font-inter"
                      >
                    <span className="w-0 group-hover:w-2 h-px bg-linear-to-r from-(--color-primary) to-(--color-accent) transition-all duration-300"></span>
                        {link.label}
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>

          {/* Central Mauritius */}
          <div>
            <h3 
              className="text-xs font-bold text-[var(--color-text)] uppercase tracking-[0.15em] mb-6 pb-2 border-b-2 border-[#BBA339]/30 font-inter"
            >
              {safeGetString(coastsMsg?.central, 'Central Mauritius')}
            </h3>
            <ul className="space-y-3">
              {[
                { label: 'Moka', href: `/${locale}/areas/moka` },
                { label: 'Ebène', href: `/${locale}/areas/ebene` },
                { label: 'Quatre Bornes', href: `/${locale}/areas/quatre-bornes` },
                { label: 'Beau Bassin - Rose Hill', href: `/${locale}/areas/beau-bassin-rose-hill` },
                { label: 'Curepipe - Forest Side', href: `/${locale}/areas/curepipe-forest-side` },
                { label: 'Vacoas - Phoenix', href: `/${locale}/areas/vacoas-phoenix` },
              ].map((link) => (
                <li key={link.href}>
                  <Link
                    href={link.href}
                    className="text-[var(--color-text-muted)] hover:text-[var(--color-primary)] transition-all duration-200 text-sm flex items-center gap-2 group hover:translate-x-1 font-inter"
                  >
                    <span className="w-0 group-hover:w-2 h-px bg-linear-to-r from-(--color-primary) to-(--color-accent) transition-all duration-300"></span>
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* Children */}
        {children && <div className="mt-12">{children}</div>}

        {/* SEO Keywords Section */}
        <div className="mt-12 pt-12 border-t border-gray-200/50">
          <div className="mb-8">
            <h3 
              className="text-sm font-bold text-[var(--color-text)] mb-4 font-inter"
            >
              {safeGetString(seoMsg?.title, 'Discover Your Dream Property')}
            </h3>
            <div className="flex flex-wrap gap-2">
              {[
                'Property for Sale Mauritius',
                'Luxury Villas Mauritius',
                'Beachfront Properties',
                'IRS Properties',
                'RES Scheme Mauritius',
                'Real Estate Investment',
                'Mauritius Property Market',
                'Expat Properties',
                'Retirement Homes',
                'Holiday Homes Mauritius',
                'Waterfront Properties',
                'Golf Course Properties',
                'Smart City Mauritius',
                'PDS Properties',
                'Property Management',
              ].map((keyword) => (
                <Link
                  key={keyword}
                  href={`/${locale}/properties?search=${encodeURIComponent(keyword.toLowerCase())}`}
                  className="px-4 py-2 bg-gray-50 hover:bg-[var(--color-primary)]/10 text-[var(--color-text-muted)] hover:text-[var(--color-primary)] text-xs font-medium rounded-full transition-all duration-200 border border-gray-200/50 hover:border-[var(--color-primary)]/30 font-inter"
                >
                  {keyword}
                </Link>
              ))}
            </div>
          </div>
        </div>

        {/* Divider */}
        <div className="relative mt-12 pt-8">
          <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-gray-300 to-transparent"></div>
          
          <div className="flex flex-col lg:flex-row justify-between items-center gap-6">
            {/* Copyright */}
            <div className="text-center lg:text-left">
              <p 
                className="text-sm font-semibold text-[var(--color-text)] mb-1 font-inter"
              >
                © {currentYear} {companyName}. {safeGetString(footerMsg?.copyright, 'All rights reserved.')}
              </p>
              <p 
                className="text-xs text-[var(--color-text-muted)] font-inter"
              >
                {displayTagline}
              </p>
            </div>

            {/* Legal Links */}
            <div className="flex flex-wrap items-center gap-3 sm:gap-6">
              <Link
                href={`/${locale}/terms`}
                className="text-sm text-[var(--color-text-muted)] hover:text-[var(--color-primary)] transition-colors font-medium font-inter"
              >
                {safeGetString(legalLinksMsg?.terms, 'Terms & Conditions')}
              </Link>
              <span className="text-gray-300">|</span>
              <Link
                href={`/${locale}/privacy`}
                className="text-sm text-[var(--color-text-muted)] hover:text-[var(--color-primary)] transition-colors font-medium font-inter"
              >
                {safeGetString(legalLinksMsg?.privacy, 'Privacy Policy')}
              </Link>
              <span className="text-gray-300">|</span>
              <Link
                href={`/${locale}/cookies`}
                className="text-sm text-[var(--color-text-muted)] hover:text-[var(--color-primary)] transition-colors font-medium font-inter"
              >
                {safeGetString(legalLinksMsg?.cookies, 'Cookie Policy')}
              </Link>
          </div>
        </div>
      </div>

        {/* Structured Data for SEO */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'RealEstateAgent',
            name: companyName || 'Allys Real Estate',
            description: displayTagline,
            contactPoint: {
              '@type': 'ContactPoint',
              contactType: 'customer service',
              ...(contactEmail && { email: contactEmail }),
              ...(contactPhone && { telephone: contactPhone }),
            },
            address: {
              '@type': 'PostalAddress',
              streetAddress: resolvedAddress.street,
              addressLocality: resolvedAddress.city,
              ...(resolvedAddress.postalCode && { postalCode: resolvedAddress.postalCode }),
              addressCountry: resolvedAddress.country,
            },
            sameAs: Object.values(resolvedSocialLinks).filter(Boolean),
          }),
        }}
      />
      </div>
    </footer>
  );
}
